import React, { ChangeEvent } from 'react';
import FontIcon from 'material-ui/FontIcon';
import { MdTextField } from 'core/components/fields/fields';
import * as Colors from 'material-ui/styles/colors';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { createForm, fieldStyles } from 'core/form/createForm';
import {
  ClosedContentProps,
  FieldText,
  FormProps,
  hideForVatRateCountry,
  lovLabel,
  lovValue,
  showForVatRateCountry
} from 'core/form/formUtils';
import StyledSelect from 'core/components/fields/styledSelect';
import { BlockComp } from 'core/components/blockComp/blockComp';
import ActiveItem from 'core/components/blockComp/activeItem';
import { Validate } from 'core/form/validate';
import { optionsSearch } from 'core/common/service/commonDataService';
import { observer } from 'mobx-react';
import {
  Invoice,
  INVOICE_TYPE,
  InvoiceItem,
  InvoiceRelation,
  INVOICERELATIONTYPE,
  linkingPerInvoiceTypes,
  relationArrayInverse
} from 'invoice/domain/invoiceDomain';
import { action } from 'mobx';
import MdEntity from 'core/metamodel/mdEntity';
import { Address, ADDRESSTYPE, COUNTRY, Party, PARTYROLE } from 'party/partyDomain';

import commonService from 'core/common/service/commonService';
import Grid, { FILTEROPERATOR, GridRow } from 'core/grid/domain/grid';
import { HasLength } from '../../core/form/rules';
import SelectSearchDialog from 'core/components/fields/selectSearchDialog';
import { formatDate, formatDateIso } from 'core/common/utils/dateUtils';
import { DEFAULT_CURRENCY_FORMAT, formatNumber, parseNumber } from 'core/common/utils/numberUtils';
import { removeItem } from 'core/common/utils/arrayUtils';
import GridConfigCondition from 'core/grid/domain/gridConfigCondition';
import { addTenantToQuery, debounce } from 'core/common/utils/utils';
import { Link } from 'react-router';
import { loc } from 'core/common/config/locales';
import { ZzIconButtonMenu } from 'core/components/buttons/icons';
import LinkIcon from 'material-ui/svg-icons/content/link';
import MenuItem from 'material-ui/MenuItem';
import invoiceService from 'invoice/service/invoiceService';
import SvgDelete = require('react-icons/lib/md/delete');
import uiService from '../../core/common/service/uiService';
import NumberField from '../../core/components/fields/numberField';


interface CustomerFormProps extends FormProps<Invoice, Invoice> {
  recalculate: (rootObject: Invoice, modifiedRows: GridRow[], toDeleteRows: GridRow[], params?: {
    [key: string]: any
  }) => Promise<any>;
}


export const InvoiceCustomerBlock = ({
                                       dataObject,
                                       entity,
                                       entities,
                                       style,
                                       fields,
                                       showLinkedInvoices
                                     }: ClosedContentProps<Invoice> & { showLinkedInvoices: boolean }) => {


  const ADDRESSTYPEEntity: MdEntity = entities.get('ADDRESSTYPE');

  let permanentAddressText = Address.addressLineFromItems(dataObject.toPermanentStreet, dataObject.toPermanentCity, dataObject.toPermanentZip, dataObject.toPermanentCountry);
  let correspondentAddressText = Address.addressLineFromItems(dataObject.toCorrespondentStreet, dataObject.toCorrespondentCity, dataObject.toCorrespondentZip, dataObject.toCorrespondentCountry);

  //labels={{toIco: toIco.textLabel, toDic: toDic.textLabel, toIcoDph: toIcoDph.textLabel}}

  return (
    <div style={style}>

      <div className="form-text-row">
        {hideForVatRateCountry(<FieldText label={fields.toIco.textLabel} value={dataObject.toIco} />, COUNTRY.RO)}
        <FieldText label={fields.toDic.textLabel} value={dataObject.toDic} />
        {showForVatRateCountry(<FieldText label={fields.toIcoDph.textLabel}
                                          value={dataObject.toIcoDph} />, COUNTRY.SK, COUNTRY.HU)}
        {dataObject.billedTo2 && <FieldText label="Second customer" value={dataObject.billedTo2.label} linkProps={{
          to: 'partyDetail',
          params: { id: dataObject.billedTo2.value }
        }} />}
      </div>

      {(permanentAddressText) ? (
        <div className="form-text-row">
          <OverlayTrigger placement="top"
                          overlay={<Tooltip id="permanentAddressTooltip"
                                            style={{ fontSize: 13 }}>{ADDRESSTYPEEntity.getLovItemLabel(ADDRESSTYPE.PERMANENT)}</Tooltip>}>
            <FontIcon className="fa fa-envelope-o"
                      color={Colors[ADDRESSTYPEEntity.getLovItemParam(ADDRESSTYPE.PERMANENT, 'color')]}
                      style={{ fontSize: 16, marginRight: 5 }} />
          </OverlayTrigger>
          <span>{permanentAddressText}</span>
        </div>
      ) : null}

      {(correspondentAddressText) ? (
        <div className="form-text-row">
          <OverlayTrigger placement="top"
                          overlay={<Tooltip id="correspondentAddressTooltip"
                                            style={{ fontSize: 13 }}>{ADDRESSTYPEEntity.getLovItemLabel(ADDRESSTYPE.CORRESPONDENT)}</Tooltip>}>
            <FontIcon className="fa fa-envelope-o"
                      color={Colors[ADDRESSTYPEEntity.getLovItemParam(ADDRESSTYPE.CORRESPONDENT, 'color')]}
                      style={{ fontSize: 16, marginRight: 5 }} />
          </OverlayTrigger>
          <span>{correspondentAddressText}</span>
        </div>
      ) : null}

      {(showLinkedInvoices && (dataObject.fromRels.length || dataObject.toRels.length)) ? (
        <div className="form-text-row">
          <LinkedInvoicesTable dataObject={dataObject}/>
        </div>
      ) : null}


    </div>
  );

};


@createForm({
  formName: 'CustomerForm',
  closedOnNew: true,
  fields: [{
    name: 'billedTo',
    validators: ['IsRequired']
  }, {
    name: 'orderNumber'
  }, {
    name: 'billedTo2'
  }, {
    name: 'toFullName',
    validators: ['IsRequired']
  }, {
    name: 'toIco'
  }, {
    name: 'toDic'
  }, {
    name: 'toIcoDph'
  }, {
    name: 'toPermanentStreet'
  }, {
    name: 'toPermanentCity'
  }, {
    name: 'toPermanentZip'
  }, {
    name: 'toPermanentCountry'
  }, {
    name: 'toCorrespondentStreet'
  }, {
    name: 'toCorrespondentCity'
  }, {
    name: 'toCorrespondentZip'
  }, {
    name: 'toCorrespondentCountry'
  }, {
    name: 'issuedBy'
  }, {
    name: 'orderedBy'
  }, {
    name: 'invoiceType',
    validators: ['IsRequired'],
    omitNullOption: true
  }]
}, InvoiceCustomerBlock)
@observer
export default class CustomerForm extends React.Component<CustomerFormProps, undefined> {

  private selectSearchDialogRef: SelectSearchDialog;


  componentWillMount() {
    let { entities, dataObject, form } = this.props;
    form.getField('orderedBy').props.options = dataObject.getTuple('orderedByOptions');
  }


  @action onBilledToChange = () => {
    let { entities, dataObject, form } = this.props;

    console.log('onBilledToChange ', dataObject.billedTo);

    //let oldBilledToLov = form.getValue(dataObject, "billedTo");

    let partyId = lovValue(dataObject.billedTo);

    let optionsPromise = (partyId != null) ? optionsSearch('Party', { contactsOf: partyId }, '*') : Promise.resolve({ options: [] });


    return Promise.all([this.props.recalculate(this.props.dataObject, null, null, { billedToChange: true }), optionsPromise])
      .then(action('onBilledToChange-cb1', ([x, options]) => {
        console.log(x);
        console.log(options);
        form.getField('orderedBy').props.options = options.options;
        dataObject.orderedBy = (options.options.length === 1) ? options.options[0].value : null;

        form.updateAllFieldsPropValue(dataObject);
        dataObject.getForm('InvoiceForm').updateAllFieldsPropValue(dataObject);
        dataObject.getForm('InvoiceAdvancedForm').updateAllFieldsPropValue(dataObject);
        //
        if (dataObject.getTuple('vatRateChange')) {
          commonService.notifyInfo('Vat rates were changed');
        }
      }));

  };


  @action onDeleteLink = (invoiceRelation: InvoiceRelation) => {
    let { dataObject, rootObject, form } = this.props;
    console.log('onDelete %o', invoiceRelation);
    removeItem(dataObject.fromRels, invoiceRelation);
    removeItem(dataObject.toRels, invoiceRelation);
    this.props.recalculate(dataObject, null, null, {});
    rootObject.$dirty = true;
  };


  onInvoiceTypeSuccess = () => {
    let { dataObject, rootObject, form, entities } = this.props;
    let doRecalculate = dataObject.toRels.length + dataObject.fromRels.length;
    dataObject.toRels = [];
    dataObject.fromRels = [];

    // zkusime najit regular pro creditNote
    let promise;

    if (lovValue(dataObject.invoiceType) === INVOICE_TYPE.CN && dataObject.copiedFromInvoiceId != undefined) {
      promise = invoiceService.readInvoice(dataObject.copiedFromInvoiceId)
        .then(invoice => {
          if ([INVOICE_TYPE.FI, INVOICE_TYPE.AD].includes(lovValue(invoice.invoiceType))) {
            dataObject.fromRels.push(new InvoiceRelation({
              toInvoice: invoice.createInvoiceInInvoiceRelation(),
              relationType: entities.get("INVOICERELATIONTYPE").getLovItem(relationArrayInverse(INVOICE_TYPE.CN, lovValue(invoice.invoiceType))),
              valueDate: formatDateIso(new Date())
            }));
            doRecalculate++;
          }
        })
    } else {
      promise = Promise.resolve(undefined);
    }

    promise.then(() => {
      if (doRecalculate) {
        return this.props.recalculate(dataObject, null, null, {});
      }
    });

  };



  addLinkedInvoice = (invoiceType: number) => {
    let { dataObject, entity, rootObject, entities } = this.props;
    console.log('addLinkedInvoice');

    new Promise((resolve, reject) => {
      return this.selectSearchDialogRef.open(resolve, reject, (grid: Grid) => {
        let linkedInvoices = dataObject.getLinkedInvoices();
        if (linkedInvoices.length) {
          if (!grid.conditionArray) grid.conditionArray = [];
          // vyloucime jiz pridane
          grid.conditionArray.push(new GridConfigCondition(grid.$gridLocationRef, {
            field: entity.getField('invoiceId'),
            operator: FILTEROPERATOR.NOTIN,
            values: linkedInvoices.map(lv => lv.value)
          }));
        }
      }, loc.i18n`Link` + ' ' + entities.get('InvoiceType').getLovItemLabel(invoiceType),  lovValue(dataObject.invoiceType) + "_" + lovValue(dataObject.billedTo) + '_' + invoiceType);
    }).then(action('close-new-party-diaglog', (linkedInvoice: Invoice) => {
      console.log('adding %o', linkedInvoice);
      if (invoiceType !== lovValue(linkedInvoice.invoiceType)) throw new Error("InvoiceType mismash")

      if (lovValue(linkedInvoice.invoiceType) === INVOICE_TYPE.PR) {
        dataObject.items = linkedInvoice.items.map(item => {
          const { invoiceItemId, invoice, ...rest } = item;
          return new InvoiceItem(rest);
        });
      }

      let invoicerelationtype = relationArrayInverse(invoiceType, lovValue(dataObject.invoiceType));
      if (invoicerelationtype) {
        dataObject.toRels.push(new InvoiceRelation({
          fromInvoice: linkedInvoice.createInvoiceInInvoiceRelation(),
          relationType: entities.get("INVOICERELATIONTYPE").getLovItem(invoicerelationtype),
          contributedAmount: linkedInvoice.totalAdjustedPrice,
          valueDate: formatDateIso(new Date())
        }));
      } else {
        dataObject.fromRels.push(new InvoiceRelation({
          toInvoice: linkedInvoice.createInvoiceInInvoiceRelation(),
          relationType: entities.get("INVOICERELATIONTYPE").getLovItem(relationArrayInverse(lovValue(dataObject.invoiceType), invoiceType)),
          contributedAmount: linkedInvoice.totalAdjustedPrice,
          valueDate: formatDateIso(new Date())
        }))
      }

      console.debug("fromRels = %o, toRels = %o", dataObject.fromRels, dataObject.toRels);

      rootObject.$dirty = true;
      return this.props.recalculate(dataObject, null, null, {});

    }), (reason) => {
      console.log("dialog rejected with reason: ", reason);
    });
  };

//  this.onResizeDebounced = debounce(this.onResize, 15, false);


  @action onChangeContributedAmount = debounce( (invoiceRelation: InvoiceRelation, newNum) => {
    let { dataObject, rootObject } = this.props;

    console.log("onChangeContributedAmount IR = %o, value = %s", invoiceRelation, newNum);
    if (newNum != null && !Number.isNaN(newNum) && typeof newNum === 'number' ) {
      invoiceRelation.contributedAmount = newNum;
      rootObject.$dirty = true;
      return this.props.recalculate(dataObject, null, null, {});
    }
  }, 1500, false);


  selectSearchDialogRefHandler = (comp) => this.selectSearchDialogRef = comp;


  render() {

    let { dataObject, rootObject, entities, formName, entity } = this.props;
    let fields = dataObject.getFormFields(formName);
    let {
      billedTo,
      billedTo2,
      toFullName,
      toIco,
      toDic,
      toIcoDph,
      orderedBy,
      orderNumber,
      invoiceType,
      toPermanentStreet,
      toPermanentCity,
      toPermanentZip,
      toPermanentCountry,
      toCorrespondentStreet,
      toCorrespondentCity,
      toCorrespondentZip,
      toCorrespondentCountry
    } = fields;

    let clearBilledTo2 = () => {
      billedTo2.props.value = undefined;
      dataObject.billedTo2 = undefined;
    };


    let openContent = (
      <div>

        {/*  TO PARTY MAIN  */}
        <div className="row">
          <div className="fx col-xs-12 col-sm-8">
            <MdTextField {...toFullName.props} />
            <Validate field={toFullName} value={toFullName.props.value} />
          </div>
          <div className="fx col-xs-12 col-sm-4">
            <MdTextField {...toDic.props} />
          </div>
        </div>

        <div className="row">
          {hideForVatRateCountry(<div className="fx col-xs-6">
            <MdTextField {...toIco.props} />
          </div>, COUNTRY.RO)}
          {showForVatRateCountry(
            (<div className="fx col-xs-6">
              <MdTextField {...toIcoDph.props} />
            </div>), COUNTRY.SK, COUNTRY.HU)}
        </div>

        {/*  TO PARTY PERMANENT ADDRESS  */}
        <div className="row">
          <div className="col-xs-12 col-sm-6">
            Permanent Address
          </div>
        </div>

        <div className="row">
          <div className="fx col-xs-12 col-sm-6">
            <MdTextField {...toPermanentStreet.props} />
          </div>
          <div className="fx col-xs-12 col-sm-6">
            <MdTextField {...toPermanentCity.props} />
          </div>
          <div className="fx col-xs-6 col-sm-6">
            <MdTextField {...toPermanentZip.props} />
          </div>
          <div className="fx col-xs-6 col-sm-6">
            <StyledSelect {...toPermanentCountry.props} />
          </div>
        </div>

        {/*  TO PARTY CORRESPONDENT ADDRESS  */}
        <div className="row">
          <div className="col-xs-12 col-sm-6">
            Correspondent Address
          </div>
        </div>

        <div className="row">
          <div className="fx col-xs-12 col-sm-6">
            <MdTextField {...toCorrespondentStreet.props} />
          </div>
          <div className="fx col-xs-12 col-sm-6">
            <MdTextField {...toCorrespondentCity.props} />
          </div>
          <div className="fx col-xs-6 col-sm-6">
            <MdTextField {...toCorrespondentZip.props} />
          </div>
          <div className="fx col-xs-6 col-sm-6">
            <StyledSelect {...toCorrespondentCountry.props} />
          </div>
        </div>

      </div>
    );

    let { recalculate, ...activeItemProps } = this.props;

    let linkingPerInvoiceType: INVOICE_TYPE[] = linkingPerInvoiceTypes[lovValue(dataObject.invoiceType)];
    let invoiceTypeEntity = entities.get('InvoiceType');

    return (

      <BlockComp noPadding={true}>
        <SelectSearchDialog ref={this.selectSearchDialogRefHandler} gridLocation="invoiceSearch"
                            showGridConfig={false} />
        <div>
          <div className="row">
            <div className="fx col-xs-8" style={{ paddingLeft: 25, paddingTop: 10 }}>
              <StyledSelect  {...billedTo.props} async={true}
                             loadOptions={optionsSearch.bind(this, 'Party', { role: [PARTYROLE.CUSTOMER_ROLE] })}
                             clearable={true} onValueEmpty={clearBilledTo2} />
              <Validate field={billedTo} value={billedTo.props.value} onSuccess={this.onBilledToChange} />
            </div>
            <div className="fx col-xs-4" style={{ paddingRight: 20, paddingTop: 10 }}>
              <StyledSelect  {...orderedBy.props} clearable={false} />
            </div>
          </div>
          <div className="row">
            <div className="fx col-xs-8" style={{ paddingLeft: 25 }}>
              <StyledSelect  {...billedTo2.props} async={true}
                             loadOptions={optionsSearch.bind(this, 'Party', { role: [PARTYROLE.CUSTOMER_ROLE] })}
                             clearable={true} disabled={!billedTo.props.value} />
              <Validate field={billedTo2} value={billedTo2.props.value} />
            </div>
            <div className="fx col-xs-4" style={{ paddingRight: 20 }}>
              <MdTextField  {...orderNumber.props} clearable={false} />
              <Validate field={orderNumber} value={orderNumber.props.value}>
                <HasLength max={30} />
              </Validate>
            </div>
          </div>
          <ActiveItem openContent={openContent}
                      closedContent={<InvoiceCustomerBlock dataObject={dataObject} entities={entities} entity={entity}
                                                           style={{ display: 'flex', flexDirection: 'column' }}
                                                           fields={fields} showLinkedInvoices={false} />}
                      closedStyle={{ paddingTop: 4, paddingBottom: 0 }} {...activeItemProps} />

          <div className="row">
            <div className="fx col-md-5 col-xs-8" style={{ paddingLeft: 25 }}>
              <StyledSelect  {...invoiceType.props} floatingLabelText="" clearable={false} />
              <Validate field={invoiceType} value={invoiceType.props.value} onSuccess={this.onInvoiceTypeSuccess} />
            </div>

            {
              !!linkingPerInvoiceType && !!linkingPerInvoiceType.length && (
                <div className="fx col-xs-4"
                     style={{ paddingRight: 25, paddingTop: 10 }}>
                  <ZzIconButtonMenu buttonIcon={<LinkIcon />}>
                    {
                      linkingPerInvoiceType.map(possibleInvType => (
                        <MenuItem primaryText={lovLabel(invoiceTypeEntity.getLovItem(possibleInvType))}
                                  onClick={this.addLinkedInvoice.bind(this, possibleInvType)}
                                  disabled={!dataObject.billedTo} />
                      ))
                    }
                  </ZzIconButtonMenu>

                </div>
              )
            }
          </div>

          <div className="row">
            <div className="fx col-xs-12" style={{ paddingLeft: 25 }}>
              <LinkedInvoicesTable dataObject={dataObject} onDelete={this.onDeleteLink} onChangeContributedAmount={this.onChangeContributedAmount} />
            </div>
          </div>

            </div>
      </BlockComp>
  );
  }
  }

interface LinkedInvoicesTableProps {
  dataObject: Invoice;
  onDelete?: Function;
  onChangeContributedAmount?: Function;
}

@observer
class LinkedInvoicesTable extends React.Component<LinkedInvoicesTableProps, undefined> {



  render() {
    const {dataObject, onDelete, onChangeContributedAmount} = this.props;
    return (
      (dataObject.fromRels.length || dataObject.toRels.length) ? (
        <table className="zz-simple-table">
          <thead>
          <tr>
            <th style={{ paddingTop: 10, paddingLeft: 0 }} colSpan={3}>{loc.i18n`Linked Invoices`}</th>
            <th>Inv Amount</th>
            <th>Contrib Amount</th>
          </tr>
          </thead>
          <tbody>
          {dataObject.fromRels.concat(dataObject.toRels).map((invoiceRelation) => {
            let otherInvoice = invoiceRelation.getOtherInvoice(dataObject);
            return (
              <tr key={otherInvoice?.value}>
                <td style={{whiteSpace: "nowrap"}}>{lovLabel(otherInvoice?.invoiceType)}</td>
                <td>{!!otherInvoice?.value && <Link to="invoiceDetail" params={{ id: otherInvoice.value }}
                                                    query={addTenantToQuery()}>{otherInvoice.label}</Link>
                }
                </td>
                <td>{formatDate(otherInvoice?.invoiceDate)}</td>
                <td>{formatNumber(otherInvoice?.totalAdjustedPrice, DEFAULT_CURRENCY_FORMAT)}</td>
                <td>
                  { (!!onChangeContributedAmount && [INVOICERELATIONTYPE.DP_TO_FI, INVOICERELATIONTYPE.AD_TO_FI, INVOICERELATIONTYPE.AD_TO_DP].includes(lovValue(invoiceRelation.relationType))) ?
                    <NumberField {...fieldStyles.small.radioButton} name={"contributedAmount_" + otherInvoice?.value } value={invoiceRelation.contributedAmount} onChange={onChangeContributedAmount.bind(this, invoiceRelation)}
                                 errorText={invoiceRelation.contributedAmount == null ? "Value is required" : (isNaN(invoiceRelation.contributedAmount) ? "Please enter a valid number" : undefined)} />
                    : formatNumber(invoiceRelation.contributedAmount, DEFAULT_CURRENCY_FORMAT)}
                </td>
                {(!!onDelete) ?
                  <td><SvgDelete style={{ cursor: 'pointer' }} onClick={onDelete.bind(this, invoiceRelation)} />
                  </td> : null}
              </tr>
            );
          })}
          </tbody>
        </table>
      ) : null
    );
  }
}



// WEBPACK FOOTER //
// ./src/invoice/component/customerForm.tsx